import { useState, useEffect, useRef, useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useProviderCategories, useProviders } from "@/hooks/useProviders";
import { toast } from "@/hooks/use-toast";
import { calculateNextServiceDate } from "@/lib/dateUtils";
import { Search, Camera, Upload, FileImage, FileText } from "lucide-react";
import { useFileUpload } from "@/hooks/useFileUpload";
import { cn } from "@/lib/utils";

const AddApplianceSheet = ({ open, onOpenChange, onSave, initialData = null }) => {
    const { user } = useAuth();
    const scrollRef = useRef(null);
    const { uploading, uploadFile, capturePhoto, selectFile } = useFileUpload();

    // Main form state
    const [name, setName] = useState("");
    const [brand, setBrand] = useState("");
    const [installationDate, setInstallationDate] = useState("");
    const [category, setCategory] = useState("");
    const [lastServiceDate, setLastServiceDate] = useState("");
    const [serviceFrequency, setServiceFrequency] = useState("");
    const [serviceFrequencyUnit, setServiceFrequencyUnit] = useState("months");
    const [providerId, setProviderId] = useState("");
    const [warrantyExpiry, setWarrantyExpiry] = useState("");
    const [providerNotifications, setProviderNotifications] = useState(true);
    const [previewNextService, setPreviewNextService] = useState(null);
    const [purchasePrice, setPurchasePrice] = useState("");
    const [purchaseDate, setPurchaseDate] = useState("");
    const [receiptFile, setReceiptFile] = useState(null);
    // Fix: warranty document upload — the sheet only ever let you upload a
    // purchase receipt; there was no field or UI to attach the actual
    // warranty document/certificate.
    const [warrantyFile, setWarrantyFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [maintenanceTasks, setMaintenanceTasks] = useState([]);

    // Provider state
    const { providers, loading: providersLoading, refetch: refetchProviders } = useProviders();
    const [showAddProvider, setShowAddProvider] = useState(false);
    const [providerSearch, setProviderSearch] = useState("");
    const [newlyAddedProvider, setNewlyAddedProvider] = useState(null);

    // Nested provider form
    const [newProviderName, setNewProviderName] = useState("");
    const [newProviderPhone, setNewProviderPhone] = useState("");
    const [addingProvider, setAddingProvider] = useState(false);

    const { categories, loading: categoriesLoading } = useProviderCategories();

    // Filter and Sort Providers (Same logic as LogMaintenanceSheet)
    const filteredProviders = useMemo(() => {
        let list = [...providers];

        if (newlyAddedProvider && !list.find(p => p.id === newlyAddedProvider.id)) {
            list.push(newlyAddedProvider);
        }

        if (providerSearch.trim()) {
            const search = providerSearch.toLowerCase();
            list = list.filter(p =>
                p.name.toLowerCase().includes(search) ||
                (p.category && p.category.toLowerCase().includes(search))
            );
        } else if (category) {
            // Default to matching appliance category
            list = list.filter(p => p.category === category);
        }

        return list.sort((a, b) => a.name.localeCompare(b.name));
    }, [providers, category, providerSearch, newlyAddedProvider]);

    // Mapping for when adding a new provider from within this sheet
    const getProviderTrade = (applianceCategory) => {
        return applianceCategory || "Other";
    };

    // Calculate next service date preview
    useEffect(() => {
        const base = lastServiceDate || installationDate;
        if (base && serviceFrequency) {
            const next = calculateNextServiceDate(base, serviceFrequency, serviceFrequencyUnit);
            setPreviewNextService(next);
        } else {
            setPreviewNextService(null);
        }
    }, [lastServiceDate, installationDate, serviceFrequency, serviceFrequencyUnit]);

    // Populate form if editing
    useEffect(() => {
        if (open && initialData) {
            setName(initialData.name || "");
            setBrand(initialData.brand || "");
            setInstallationDate(initialData.installation_date || "");
            setCategory(initialData.category || "");
            setLastServiceDate(initialData.last_service_date || "");
            setServiceFrequency(initialData.service_frequency?.toString() || "");
            setServiceFrequencyUnit(initialData.service_frequency_unit || "months");
            setProviderId(initialData.provider_id || "");
            setWarrantyExpiry(initialData.warranty_expiry || "");
            setProviderNotifications(initialData.provider_notifications ?? true);
            setPurchasePrice(initialData.purchase_price?.toString() || "");
            setPurchaseDate(initialData.purchase_date || "");
            setReceiptFile(initialData.receipt_url ? { url: initialData.receipt_url, name: "Proof of Purchase", type: "image/jpeg" } : null);
            setWarrantyFile(initialData.warranty_url ? { url: initialData.warranty_url, name: "Warranty Document", type: "application/pdf" } : null);
            setMaintenanceTasks(initialData.maintenance_tasks || []);
        } else if (open && !initialData) {
            resetForm();
        }
    }, [open, initialData]);

    const handleAddProvider = async () => {
        if (!newProviderName || !user) return;

        setAddingProvider(true);
        const providerCategoryName = getProviderTrade(category);

        const dbCategory = categories.find(c =>
            c.name.toLowerCase() === providerCategoryName.toLowerCase()
        );
        const categoryId = dbCategory ? dbCategory.id : providerCategoryName.toLowerCase().replace(/\s+/g, "-");

        const { data, error } = await supabase
            .from("providers")
            .insert({
                name: newProviderName,
                category: providerCategoryName,
                category_id: categoryId,
                phone: newProviderPhone || null,
                is_verified: false,
                is_featured: false,
                added_by_user_id: user.id,
            })
            .select("id, name, category")
            .single();

        if (error) {
            toast({
                title: "Error",
                description: "Failed to add provider",
                variant: "destructive",
            });
        } else if (data) {
            setNewlyAddedProvider(data);
            setProviderId(data.id);
            toast({
                title: "Provider Added",
                description: `${newProviderName} has been added.`,
            });
            setShowAddProvider(false);
            setNewProviderName("");
            setNewProviderPhone("");
            refetchProviders();
        }
        setAddingProvider(false);
    };

    const resetForm = () => {
        setName("");
        setBrand("");
        setInstallationDate("");
        setCategory("");
        setLastServiceDate("");
        setServiceFrequency("");
        setServiceFrequencyUnit("months");
        setProviderId("");
        setWarrantyExpiry("");
        setProviderNotifications(true);
        setShowAddProvider(false);
        setNewProviderName("");
        setNewProviderPhone("");
        setPurchasePrice("");
        setPurchaseDate("");
        setReceiptFile(null);
        setWarrantyFile(null);
        setMaintenanceTasks([]);
    };

    const handleSave = async () => {
        // Validation with feedback
        if (!name) {
            toast({ title: "Missing Field", description: "Please enter an appliance name.", variant: "destructive" });
            return;
        }
        if (!brand) {
            toast({ title: "Missing Field", description: "Please enter the brand.", variant: "destructive" });
            return;
        }
        if (!installationDate) {
            toast({ title: "Missing Field", description: "Please enter the installation date.", variant: "destructive" });
            return;
        }
        if (!category) {
            toast({ title: "Missing Field", description: "Please select a category.", variant: "destructive" });
            return;
        }

        const installYear = new Date(installationDate).getFullYear();
        if (installYear < 1900 || installYear > 2100) {
            toast({ title: "Invalid Date", description: "Please enter a valid installation year.", variant: "destructive" });
            return;
        }

        if (lastServiceDate) {
            const serviceYear = new Date(lastServiceDate).getFullYear();
            if (serviceYear < 1900 || serviceYear > 2100) {
                toast({ title: "Invalid Date", description: "Please enter a valid last service year.", variant: "destructive" });
                return;
            }
        }

        setLoading(true);
        try {
            // Process and calculate next service date for each subtask
            const processedTasks = maintenanceTasks.map(task => {
                const base = task.last_service_date || installationDate || new Date().toISOString().split("T")[0];
                const next = calculateNextServiceDate(base, task.frequency || 1, task.frequency_unit || "months");
                return {
                    ...task,
                    frequency: task.frequency ? parseInt(task.frequency, 10) : 1,
                    last_service_date: task.last_service_date || installationDate || new Date().toISOString().split("T")[0],
                    next_service_date: next
                };
            });

            const success = await onSave({
                name,
                brand,
                installationDate,
                category,
                lastServiceDate: lastServiceDate || undefined,
                serviceFrequency: serviceFrequency ? parseInt(serviceFrequency) : undefined,
                serviceFrequencyUnit: serviceFrequencyUnit || undefined,
                providerId: providerId || undefined,
                warrantyExpiry: warrantyExpiry || undefined,
                providerNotifications: providerNotifications,
                purchasePrice: purchasePrice ? parseFloat(purchasePrice) : undefined,
                purchaseDate: purchaseDate || undefined,
                receiptUrl: receiptFile?.url || undefined,
                warrantyUrl: warrantyFile?.url || undefined,
                maintenanceTasks: processedTasks,
            });

            if (success) {
                resetForm();
                onOpenChange(false);
            }
        } catch (error) {
            console.error("Save error:", error);
            toast({
                title: "Save Failed",
                description: "There was an error saving your appliance. Please try again.",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = (open) => {
        if (!open) resetForm();
        onOpenChange(open);
    };

    const handleFocus = (e) => {
        setTimeout(() => {
            e.target.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 350);
    };

    // Fix: generalized to accept which setter/folder to upload into, so the
    // same camera/file-picker flow can drive both the receipt upload and the
    // new warranty-document upload instead of being hardcoded to receipts.
    const handleCameraCapture = async (setFile, folder = "receipts") => {
        const file = await capturePhoto();
        if (file) {
            const result = await uploadFile(file, folder);
            if (result) setFile(result);
        }
    };

    const handleFileSelect = async (setFile, folder = "receipts") => {
        const file = await selectFile("application/pdf,image/*");
        if (file) {
            const result = await uploadFile(file, folder);
            if (result) setFile(result);
        }
    };

    const renderFileUpload = (label, file, setFile, folder = "receipts") => (
        <div className="space-y-2">
            <Label className="text-sm font-medium text-foreground">{label}</Label>
            {!file ? (
                <div className={cn(
                    "w-full border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-2 py-4 transition-colors",
                    uploading ? "opacity-50 pointer-events-none bg-muted/20" : "hover:border-primary/50 hover:bg-accent/50"
                )}>
                    {uploading ? (
                        <Loader2 className="w-5 h-5 text-primary animate-spin" />
                    ) : (
                        <div className="flex gap-3">
                            <button type="button" onClick={() => handleCameraCapture(setFile, folder)} className="p-2 rounded-full bg-primary/10 hover:bg-primary/20">
                                <Camera className="w-5 h-5 text-primary" />
                            </button>
                            <button type="button" onClick={() => handleFileSelect(setFile, folder)} className="p-2 rounded-full bg-primary/10 hover:bg-primary/20">
                                <Upload className="w-5 h-5 text-primary" />
                            </button>
                        </div>
                    )}
                    <span className="text-xs text-muted-foreground">Photo or Document</span>
                </div>
            ) : (
                <div className="relative w-full p-3 bg-muted/30 rounded-xl border border-border flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                        {file.type?.startsWith("image/") ? <FileImage className="w-4 h-4 text-primary" /> : <FileText className="w-4 h-4 text-primary" />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">{file.name}</p>
                        <p className="text-[10px] text-muted-foreground">Uploaded</p>
                    </div>
                    <button type="button" onClick={() => setFile(null)} className="p-1 hover:bg-muted rounded-full">
                        <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent
                side="bottom"
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="max-h-[85vh] rounded-t-3xl flex flex-col p-0"
                style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
            >
                <SheetHeader className="pb-4 flex-shrink-0 px-6 pt-8">
                    <SheetTitle className="text-xl font-bold text-foreground">
                        {initialData ? "Edit Appliance" : "Add New Appliance"}
                    </SheetTitle>
                </SheetHeader>

                <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 space-y-4 pb-4 px-6">
                    {/* Basic Info */}
                    <div className="space-y-2">
                        <Label htmlFor="appliance-name">Appliance Name <span className="text-destructive">*</span></Label>
                        <Input
                            id="appliance-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onFocus={handleFocus}
                            placeholder="e.g., Refrigerator, Geyser"
                            className="rounded-xl w-full"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="appliance-brand">Brand <span className="text-destructive">*</span></Label>
                        <Input
                            id="appliance-brand"
                            value={brand}
                            onChange={(e) => setBrand(e.target.value)}
                            onFocus={handleFocus}
                            placeholder="e.g., Samsung, Bosch"
                            className="rounded-xl w-full"
                        />
                    </div>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="installation-date">Installation Date <span className="text-destructive">*</span></Label>
                            <Input
                                id="installation-date"
                                type="date"
                                value={installationDate}
                                onChange={(e) => {
                                    setInstallationDate(e.target.value);
                                    if (!lastServiceDate) {
                                        setLastServiceDate(e.target.value);
                                    }
                                }}
                                onFocus={handleFocus}
                                className="rounded-xl w-full"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="category">Category <span className="text-destructive">*</span></Label>
                            <Select value={category} onValueChange={setCategory}>
                                <SelectTrigger className="rounded-xl w-full">
                                    <SelectValue placeholder={categoriesLoading ? "Loading..." : "Select"} />
                                </SelectTrigger>
                                <SelectContent>
                                    {categories.map((cat) => (
                                        <SelectItem key={cat.id} value={cat.name}>
                                            {cat.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="warranty-expiry">Warranty Expiry</Label>
                        <Input
                            id="warranty-expiry"
                            type="date"
                            value={warrantyExpiry}
                            onChange={(e) => setWarrantyExpiry(e.target.value)}
                            onFocus={handleFocus}
                            className="rounded-xl w-full"
                        />
                    </div>

                    {renderFileUpload("Warranty Document", warrantyFile, setWarrantyFile, "warranties")}

                    {/* Financial Info */}
                    <div className="pt-2 border-t border-border">
                        <h3 className="text-sm font-medium text-muted-foreground mb-3">
                            Financial Info
                        </h3>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2 min-w-0">
                                    <Label htmlFor="purchase-price">Purchase Price</Label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium">R</span>
                                        <Input
                                            id="purchase-price"
                                            type="number"
                                            value={purchasePrice}
                                            onChange={(e) => setPurchasePrice(e.target.value)}
                                            className="rounded-xl pl-7"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2 min-w-0">
                                    <Label htmlFor="purchase-date">Purchase Date</Label>
                                    <Input
                                        id="purchase-date"
                                        type="date"
                                        value={purchaseDate}
                                        onChange={(e) => setPurchaseDate(e.target.value)}
                                        className="rounded-xl w-full"
                                    />
                                </div>
                            </div>
                            {renderFileUpload("Proof of Purchase (Receipt)", receiptFile, setReceiptFile, "receipts")}
                        </div>
                    </div>

                    {/* Service Info */}
                    <div className="pt-4 border-t border-border">
                        <h3 className="text-sm font-medium text-muted-foreground mb-3">
                            Service Schedule
                        </h3>

                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="last-service">Last Service Date</Label>
                                <Input
                                    id="last-service"
                                    type="date"
                                    value={lastServiceDate}
                                    onChange={(e) => setLastServiceDate(e.target.value)}
                                    onFocus={handleFocus}
                                    className="rounded-xl w-full"
                                />
                                <p className="text-[10px] text-muted-foreground mt-1 px-1">
                                    Tip: This usually defaults to your installation date.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label htmlFor="service-frequency">Frequency</Label>
                                    <Input
                                        id="service-frequency"
                                        type="number"
                                        value={serviceFrequency}
                                        onChange={(e) => setServiceFrequency(e.target.value)}
                                        onFocus={handleFocus}
                                        placeholder="e.g., 12"
                                        className="rounded-xl"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="service-frequency-unit">Unit</Label>
                                    <Select value={serviceFrequencyUnit} onValueChange={setServiceFrequencyUnit}>
                                        <SelectTrigger id="service-frequency-unit" className="rounded-xl">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="months">Months</SelectItem>
                                            <SelectItem value="years">Years</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {previewNextService && (
                                <div className="mt-1 p-2 bg-primary/5 rounded-lg border border-primary/10">
                                    <p className="text-xs text-primary font-medium">
                                        Calculated Next Service: {new Date(previewNextService).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </p>
                                </div>
                            )}

                            {/* Provider Selection */}
                            <div className="space-y-2">
                                <Label htmlFor="provider-select">Service Provider</Label>
                                {!showAddProvider ? (
                                    <Select
                                        value={providerId}
                                        onValueChange={(value) => {
                                            if (value === "add-new") {
                                                setShowAddProvider(true);
                                            } else {
                                                setProviderId(value);
                                            }
                                        }}
                                        disabled={providersLoading}
                                    >
                                        <SelectTrigger id="provider-select" className="rounded-xl bg-white/50">
                                            <SelectValue placeholder={providersLoading ? "Loading..." : "Select provider (optional)"} />
                                        </SelectTrigger>
                                        <SelectContent className="bg-popover border border-border z-[100] max-h-[300px]">
                                            <div className="px-2 py-2 sticky top-0 bg-popover z-10 border-b mb-1">
                                                <div className="relative">
                                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                                    <Input
                                                        id="provider-search"
                                                        aria-label="Search providers"
                                                        placeholder="Search all providers..."
                                                        value={providerSearch}
                                                        onChange={(e) => setProviderSearch(e.target.value)}
                                                        className="h-8 pl-8 text-xs rounded-lg"
                                                        onClick={(e) => e.stopPropagation()}
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        onKeyDown={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                            </div>

                                            <div className="overflow-y-auto">
                                                {filteredProviders.length === 0 ? (
                                                    <div className="py-6 text-center text-xs text-muted-foreground">
                                                        No providers found matching "{providerSearch}"
                                                    </div>
                                                ) : (
                                                    <>
                                                        {filteredProviders.map((provider) => (
                                                            <SelectItem key={provider.id} value={provider.id}>
                                                                <div className="flex items-center justify-between w-full gap-2">
                                                                    <span>{provider.name}</span>
                                                                    <span className="text-[10px] opacity-50 italic">
                                                                        {provider.category}
                                                                    </span>
                                                                </div>
                                                            </SelectItem>
                                                        ))}
                                                    </>
                                                )}

                                                <div className="border-t mt-1 pt-1">
                                                    <SelectItem value="add-new" className="text-primary font-bold">
                                                        <div className="flex items-center gap-2">
                                                            <Plus className="w-4 h-4 ml-1 mr-2" />
                                                            Add New Provider
                                                        </div>
                                                    </SelectItem>
                                                </div>
                                            </div>
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <div className="p-3 border border-border rounded-xl bg-muted/30 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium">Add New Provider</span>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    setShowAddProvider(false);
                                                    setNewProviderName("");
                                                    setNewProviderPhone("");
                                                }}
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                        <Input
                                            id="new-provider-name"
                                            aria-label="Provider name"
                                            value={newProviderName}
                                            onChange={(e) => setNewProviderName(e.target.value)}
                                            onFocus={handleFocus}
                                            placeholder="Provider name *"
                                            className="rounded-xl"
                                        />
                                        <Input
                                            id="new-provider-phone"
                                            aria-label="Provider phone"
                                            value={newProviderPhone}
                                            onChange={(e) => setNewProviderPhone(e.target.value)}
                                            onFocus={handleFocus}
                                            placeholder="Phone number (optional)"
                                            className="rounded-xl"
                                        />
                                        <Button
                                            type="button"
                                            size="sm"
                                            className="w-full rounded-xl"
                                            onClick={handleAddProvider}
                                            disabled={!newProviderName || addingProvider}
                                        >
                                            {addingProvider ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                "Add Provider"
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* Provider Notification Toggle */}
                            <div className="space-y-3 pt-2">
                                <div className="flex items-center justify-between p-4 bg-primary/5 rounded-xl border border-primary/10">
                                    <div className="flex-1 pr-4">
                                        <p className="text-sm font-bold text-foreground">Email Reminder to Provider</p>
                                        <p className="text-[10px] text-muted-foreground mt-0.5 leading-relaxed">
                                            If enabled, we will automatically email this provider 30 days before service is due.
                                        </p>
                                    </div>
                                    <Switch
                                        checked={providerNotifications}
                                        onCheckedChange={setProviderNotifications}
                                    />
                                </div>
                                <div className="flex items-start gap-2 px-1">
                                    <div className="mt-1 p-1 bg-amber-500/10 rounded-full">
                                        <Plus className="w-2.5 h-2.5 text-amber-600 rotate-45" />
                                    </div>
                                    <p className="text-[10px] text-muted-foreground leading-relaxed">
                                        <strong>Note:</strong> This only works if "Provider Notifications" is also enabled in your global <span className="text-primary font-bold">Profile &gt; Notifications</span> settings.
                                    </p>
                                </div>
                            </div>

                            {/* Routine Maintenance Sub-Tasks */}
                            <div className="pt-4 border-t border-border">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-medium text-muted-foreground">
                                        Routine Maintenance Tasks
                                    </h3>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            setMaintenanceTasks(prev => [
                                                ...prev,
                                                {
                                                    id: Math.random().toString(36).substr(2, 9),
                                                    name: "",
                                                    frequency: "",
                                                    frequency_unit: "months",
                                                    last_service_date: ""
                                                }
                                            ]);
                                        }}
                                        className="h-8 rounded-lg gap-1 text-xs border-primary/20 bg-primary/5 text-primary font-semibold"
                                    >
                                        <Plus className="w-3.5 h-3.5" />
                                        Add Task
                                    </Button>
                                </div>

                                {maintenanceTasks.length === 0 ? (
                                    <p className="text-xs text-muted-foreground italic px-1">
                                        No custom sub-tasks added yet. Add tasks like weekly filter cleaning.
                                    </p>
                                ) : (
                                    <div className="space-y-4">
                                        {maintenanceTasks.map((task) => (
                                            <div key={task.id} className="p-3 border border-border rounded-2xl bg-muted/20 relative space-y-3">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setMaintenanceTasks(prev => prev.filter(t => t.id !== task.id));
                                                    }}
                                                    className="absolute top-2 right-2 p-1 hover:bg-muted rounded-full text-muted-foreground hover:text-destructive transition-colors"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>

                                                <div className="space-y-1.5 pr-6">
                                                    <Label htmlFor={`task-name-${task.id}`} className="text-xs font-semibold">Task Name</Label>
                                                    <Input
                                                        id={`task-name-${task.id}`}
                                                        value={task.name}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            setMaintenanceTasks(prev => prev.map(t => t.id === task.id ? { ...t, name: val } : t));
                                                        }}
                                                        placeholder="e.g., Clean Filter, Empty Bin"
                                                        className="h-9 rounded-xl text-sm w-full"
                                                    />
                                                </div>

                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className="space-y-1.5 min-w-0">
                                                        <Label htmlFor={`task-freq-${task.id}`} className="text-xs font-semibold">Frequency</Label>
                                                        <Input
                                                            id={`task-freq-${task.id}`}
                                                            type="number"
                                                            value={task.frequency}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                setMaintenanceTasks(prev => prev.map(t => t.id === task.id ? { ...t, frequency: val } : t));
                                                            }}
                                                            placeholder="e.g., 7, 2"
                                                            className="h-9 rounded-xl text-sm w-full"
                                                        />
                                                    </div>
                                                    <div className="space-y-1.5 min-w-0">
                                                        <Label htmlFor={`task-unit-${task.id}`} className="text-xs font-semibold">Unit</Label>
                                                        <Select
                                                            value={task.frequency_unit}
                                                            onValueChange={(val) => {
                                                                setMaintenanceTasks(prev => prev.map(t => t.id === task.id ? { ...t, frequency_unit: val } : t));
                                                            }}
                                                        >
                                                            <SelectTrigger id={`task-unit-${task.id}`} className="h-9 rounded-xl text-sm w-full">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="days">Days</SelectItem>
                                                                <SelectItem value="weeks">Weeks</SelectItem>
                                                                <SelectItem value="months">Months</SelectItem>
                                                                <SelectItem value="years">Years</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>

                                                <div className="space-y-1.5">
                                                    <Label htmlFor={`task-last-date-${task.id}`} className="text-xs font-semibold">Last Done Date (Optional)</Label>
                                                    <Input
                                                        id={`task-last-date-${task.id}`}
                                                        type="date"
                                                        value={task.last_service_date || ""}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            setMaintenanceTasks(prev => prev.map(t => t.id === task.id ? { ...t, last_service_date: val } : t));
                                                        }}
                                                        className="h-9 rounded-xl text-sm w-full"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                setMaintenanceTasks(prev => [
                                                    ...prev,
                                                    {
                                                        id: Math.random().toString(36).substr(2, 9),
                                                        name: "",
                                                        frequency: "",
                                                        frequency_unit: "months",
                                                        last_service_date: ""
                                                    }
                                                ]);
                                            }}
                                            className="w-full h-10 rounded-xl gap-2 border-dashed border-2 text-primary font-semibold hover:bg-primary/5"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Add Another Task
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex-shrink-0 pt-2 px-6">
                    <Button
                        onClick={handleSave}
                        className="w-full rounded-xl py-5 text-white"
                        disabled={loading}
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Appliance"}
                    </Button>
                </div>
            </SheetContent>
        </Sheet >
    );
};

export default AddApplianceSheet;
